import {
    type ActingForBook,
    type FrontendKey,
    type Persona,
    personaToFrontend,
    grantedFrontends,
    grantsAdministration,
    isDeskRouteBarred,
    isDeskSectionVisible,
} from '@septima/contract';
import { intendedPersonaForFrontend } from './persona-switch';
import { navFor } from './feature-registry';

// The four staff views, in canonical order. Login lands on the first granted
// view; the sidebar switcher renders the user's granted subset.
export const FRONTEND_KEYS: readonly FrontendKey[] = ['desk', 'ops', 'comply', 'view'];

export interface FrontendMeta {
    key: FrontendKey;
    /** Full brand label, e.g. shown in the sidebar header. */
    label: string;
    /** Short label, e.g. shown on the switcher buttons. */
    shortLabel: string;
}

export const FRONTENDS: Record<FrontendKey, FrontendMeta> = {
    desk: { key: 'desk', label: 'Septima Desk', shortLabel: 'Desk' },
    ops: { key: 'ops', label: 'Septima Ops', shortLabel: 'Ops' },
    comply: { key: 'comply', label: 'Septima Comply', shortLabel: 'Comply' },
    view: { key: 'view', label: 'Septima View', shortLabel: 'View' },
};

/**
 * One button in the sidebar view switcher.
 *
 * A view contributes one button. The Desk view contributes one button for EACH
 * IM book an assistant acts for, so the row reads "JBD | WD | Ops |
 * View" rather than "Desk | Ops | View". `actingForUserId` names the book the
 * button opens, and it is null for a plain view button.
 */
export interface SwitcherButton {
    /** Stable React key: the frontend key, plus the book id when there is one. */
    key: string;
    /** The text on the button — a view short label, or an IM desk code. */
    label: string;
    /** The tooltip and the accessible name. */
    title: string;
    /** The view the button opens. */
    frontend: FrontendKey;
    /** The IM book the button opens, or null for a plain view button. */
    actingForUserId: string | null;
    /** The button that shows the view and the book on screen now. */
    active: boolean;
}

/**
 * The view-switcher buttons for a session, in canonical view order.
 *
 * The Desk view expands into one button for each assigned IM book, because an
 * assistant reads one book at a time and the switch between books is the same
 * gesture as the switch between views. A user who acts for nobody keeps the
 * single "Desk" button. An IM who acts for other IMs gets the "Desk" button
 * for their own book, then one button for each assigned book.
 *
 * Pure, so the button set is unit-testable without a shell.
 */
export function switcherButtonsFor(
    granted: readonly FrontendKey[],
    frontend: FrontendKey,
    actingFor: readonly ActingForBook[],
    activeActingForUserId: string | null,
    selfUserId: string | null = null,
): SwitcherButton[] {
    const out: SwitcherButton[] = [];
    for (const key of FRONTEND_KEYS) {
        if (!granted.includes(key)) continue;
        if (key === 'desk' && actingFor.length > 0) {
            for (const book of actingFor) {
                const own = book.userId === selfUserId;
                out.push({
                    key: `desk:${book.userId}`,
                    label: own ? FRONTENDS.desk.shortLabel : book.label,
                    title: own
                        ? FRONTENDS.desk.label
                        : book.name
                          ? `the book of ${book.name}`
                          : book.label,
                    frontend: 'desk',
                    actingForUserId: book.userId,
                    active: frontend === 'desk' && book.userId === activeActingForUserId,
                });
            }
            continue;
        }
        out.push({
            key,
            label: FRONTENDS[key].shortLabel,
            title: FRONTENDS[key].label,
            frontend: key,
            actingForUserId: null,
            active: frontend === key,
        });
    }
    return out;
}

// Sidebar sections for each frontend
export interface SidebarSection {
    key: string;
    label: string;
    href: string;
    icon: string;
    badge?: number | string;
    /**
     * Legacy / equivalent routes that resolve to this same section — used for
     * active-state highlighting and route authorisation when a row's canonical
     * `href` was renamed (e.g. the desk landing moved `/desk/my-book` →
     * `/desk/dashboard` but the old URL still renders the same page).
     */
    aliases?: string[];
    /**
     * Globally retired: removed from the sidebar AND from route authorisation —
     * a 403 for EVERY persona (filtered in `visibleSectionsFor`, which both the
     * sidebar and `isRouteAuthorised` derive from). Distinct from the
     * per-persona `isDeskSectionVisible` policy, which only hides a row for a
     * single persona. The section + its route stay declared (reversible) — the
     * page is reachable again the moment the flag is dropped.
     */
    hidden?: boolean;
    /**
     * Removed from the sidebar ONLY: the row does not render, but the route
     * stays authorised for every persona that could reach it before, so a deep
     * link into the page still opens it. Use this when a page has a real
     * entry point outside the sidebar — Desk → Document Drafts is reached from
     * the MCP draft-approval link (SEP-213). Contrast `hidden`, which retires
     * the route as well and 403s it.
     */
    sidebarHidden?: boolean;
    /**
     * Work-in-progress: the page is reachable and renders, but is not finished.
     * The row leaves its topical sidebar group for the trailing "Work in
     * Progress" group (below System), it carries a "WIP" badge, and the page
     * shows a banner warning it is not complete yet. All three come from this
     * one flag — the placement in `groupSectionsForSidebar`, the badge in
     * `AppSidebar`, the banner in `FrontendLayout` — so the marker, the
     * position and the page notice can never drift. Single source of truth for
     * the "is this page done?" question: drop the flag and the row returns to
     * its topic group with no other edit.
     */
    wip?: boolean;
    /** The id of the feature that contributes the row (ADR-0027). */
    feature?: string;
}

// The sidebar rows of each view. Each feature file contributes its own rows
// (`nav` of `defineWebFeature`, ADR-0027), so a view shows only the rows of
// the features that the Feature Manifest enables. The `order` of a row sets
// its place in the table and in its group.
function sectionsFromNav(frontend: FrontendKey): SidebarSection[] {
    return navFor(frontend).map((n) => ({
        key: n.key,
        label: n.label,
        href: n.href,
        icon: n.icon,
        ...(n.aliases ? { aliases: [...n.aliases] } : {}),
        ...(n.hidden ? { hidden: true } : {}),
        ...(n.sidebarHidden ? { sidebarHidden: true } : {}),
        ...(n.wip ? { wip: true } : {}),
        feature: n.feature,
    }));
}

export const deskSections: SidebarSection[] = sectionsFromNav('desk');
export const opsSections: SidebarSection[] = sectionsFromNav('ops');
export const complySections: SidebarSection[] = sectionsFromNav('comply');
export const viewSections: SidebarSection[] = sectionsFromNav('view');

/** The sidebar key for the admin-only View → Administration section. */
export const ADMINISTRATION_SECTION_KEY = 'administration';

// --- Sidebar grouping -------------------------------------------------------

export interface SidebarGroupHeading {
    /** The id that a sidebar row names in its `group`. */
    id: string;
    label: string;
}

export interface SidebarGroup extends SidebarGroupHeading {
    /** Section keys, in the order the rows render under the group heading. */
    keys: string[];
}

/**
 * The labelled groups of each view, in the order a user reads them down the
 * sidebar. A sidebar row names its group (`group` of its nav contribution),
 * and its `order` sets its place in the group.
 */
const SIDEBAR_GROUP_HEADINGS: Record<FrontendKey, SidebarGroupHeading[]> = {
    desk: [
        { id: 'overview', label: 'Overview' },
        { id: 'septima-crm', label: 'Septima CRM' },
        { id: 'tasks-alerts', label: 'Tasks & Alerts' },
        { id: 'advice-suitability', label: 'Advice & Suitability' },
        { id: 'automation', label: 'Automation' },
        { id: 'personal', label: 'Personal' },
        { id: 'system', label: 'System' },
    ],
    ops: [
        { id: 'overview', label: 'Overview' },
        { id: 'processing', label: 'Processing' },
        { id: 'cash', label: 'Cash' },
        { id: 'client-operations', label: 'Client Operations' },
        { id: 'reporting', label: 'Reporting' },
        { id: 'automation', label: 'Automation' },
        { id: 'personal', label: 'Personal' },
        { id: 'system', label: 'System' },
    ],
    comply: [
        { id: 'overview', label: 'Overview' },
        { id: 'consumer-duty', label: 'Consumer Duty' },
        { id: 'clients-kyc', label: 'Clients & KYC' },
        { id: 'monitoring', label: 'Monitoring' },
        { id: 'pa-dealing', label: 'PA Dealing' },
        { id: 'governance', label: 'Governance' },
        { id: 'automation', label: 'Automation' },
        { id: 'personal', label: 'Personal' },
        { id: 'system', label: 'System' },
    ],
    view: [
        { id: 'overview', label: 'Overview' },
        { id: 'performance', label: 'Performance' },
        { id: 'intelligence', label: 'Intelligence' },
        { id: 'clients-risk', label: 'Clients & Risk' },
        { id: 'platform', label: 'Platform' },
        { id: 'automation', label: 'Automation' },
        { id: 'personal', label: 'Personal' },
        { id: 'administration', label: 'Administration' },
    ],
};

/**
 * The labelled groups the sidebar renders, with the section keys of each
 * group. This map, not the section tables, decides the ORDER and the headings
 * a user sees. `groupSectionsForSidebar` resolves the keys against the
 * persona-visible sections, so a key the active persona cannot see is
 * dropped silently.
 *
 * The keys come from the nav contributions of the enabled features. A row
 * with no sidebar entry (`hidden` or `sidebarHidden`) is in no group.
 *
 * A row whose group is not in the headings is a bug, not a feature: a
 * `sidebar-groups.test` guard fails the build for an unclaimed row, because
 * such a row used to fall into a stray "More" heading at the foot of the
 * sidebar (SEP-213).
 */
export const SIDEBAR_GROUPS: Record<FrontendKey, SidebarGroup[]> = Object.fromEntries(
    FRONTEND_KEYS.map((frontend) => {
        const rows = navFor(frontend).filter((n) => !n.hidden && !n.sidebarHidden);
        return [
            frontend,
            SIDEBAR_GROUP_HEADINGS[frontend].map((heading) => ({
                ...heading,
                keys: rows.filter((n) => n.group === heading.id).map((n) => n.key),
            })),
        ];
    }),
) as Record<FrontendKey, SidebarGroup[]>;

function sectionsForFrontend(frontend: FrontendKey): SidebarSection[] {
    switch (frontend) {
        case 'desk':
            return deskSections;
        case 'ops':
            return opsSections;
        case 'comply':
            return complySections;
        case 'view':
            return viewSections;
    }
}

/**
 * The sidebar sections visible for the active persona, after the policy filter
 * (ADR-0004). Resolves the persona to its frontend (`im-assistant → desk`),
 * then:
 *   - on Desk, omits the vulnerability row and the client-model tiers for an
 *     `im-assistant` (`isDeskSectionVisible`);
 *   - on View, reveals the Administration row only when the granted tokens
 *     include `admin` (`showAdministration`).
 *
 * Replaces the bare `getSectionsForFrontend(frontend)` call the shell used to
 * make — the single place section visibility is decided.
 *
 * This is the ACCESS answer, so it keeps a `sidebarHidden` row: that row has no
 * sidebar entry but its route is still authorised. For the rows to RENDER, use
 * `sidebarRowsFor`.
 */
export function visibleSectionsFor(
    activePersona: Persona,
    grantedTokens: readonly string[],
): SidebarSection[] {
    const frontend = personaToFrontend(activePersona);
    const showAdmin = grantsAdministration(grantedTokens);
    return sectionsForFrontend(frontend).filter((section) => {
        // Globally retired rows (`hidden`) are gone for every persona — drop
        // them from the sidebar AND from route authorisation (this function
        // backs `isRouteAuthorised`, so a hidden section's route 403s).
        if (section.hidden) return false;
        if (section.key === ADMINISTRATION_SECTION_KEY) return showAdmin;
        if (frontend === 'desk') return isDeskSectionVisible(activePersona, section.key);
        return true;
    });
}

/**
 * The sidebar ROWS for the active persona: the visible sections, less the rows
 * that carry `sidebarHidden` (off the sidebar, route still open). The sidebar
 * renders these; `isRouteAuthorised` does not use them.
 */
export function sidebarRowsFor(
    activePersona: Persona,
    grantedTokens: readonly string[],
): SidebarSection[] {
    return visibleSectionsFor(activePersona, grantedTokens).filter(
        (section) => !section.sidebarHidden,
    );
}

/** The heading of the trailing group that collects the unfinished pages. */
export const WIP_GROUP_LABEL = 'Work in Progress';

/**
 * Fold the sidebar rows into their labelled groups, in `SIDEBAR_GROUPS` order.
 * An empty group is dropped. A row that no group claims is appended under
 * "More", so a missing key can never hide a row — the `sidebar-groups` guard
 * fails the build for that case rather than letting the stray heading ship.
 *
 * A `wip` row leaves its topical group and joins ONE trailing "Work in
 * Progress" group, after System, so the finished sidebar reads clean and every
 * unfinished page sits together at the foot of it. `SIDEBAR_GROUPS` still
 * claims the key under its topic — the flag alone decides the placement, so a
 * page that lands returns to its topic with no group edit. The rows keep their
 * section-table order inside the group.
 */
export function groupSectionsForSidebar(
    frontend: FrontendKey,
    sections: SidebarSection[],
): Array<{ label: string; items: SidebarSection[] }> {
    const wipRows = sections.filter((section) => section.wip);
    const readyRows = sections.filter((section) => !section.wip);

    const byKey = new Map(readyRows.map((section) => [section.key, section]));
    const used = new Set<string>();
    const groups = SIDEBAR_GROUPS[frontend]
        .map((group) => {
            const items = group.keys
                .map((key) => byKey.get(key))
                .filter((section): section is SidebarSection => Boolean(section));
            items.forEach((item) => used.add(item.key));
            return { label: group.label, items };
        })
        .filter((group) => group.items.length > 0);

    const remaining = readyRows.filter((section) => !used.has(section.key));
    if (remaining.length > 0) groups.push({ label: 'More', items: remaining });
    if (wipRows.length > 0) groups.push({ label: WIP_GROUP_LABEL, items: wipRows });
    return groups;
}

// --- Route authorisation (ADR-0004 / issue #209) ----------------------------
//
// The sidebar HIDES unauthorised entries, but a user who types or saves a URL
// can still reach the page. This seam closes that at the frontend: it decides,
// for a pathname, whether the ACTIVE persona may render the page IN PLACE — the
// same verdict the backend enforces with a 403. It is derived from the SAME
// policy the sidebar uses (`personaToFrontend`, `isDeskSectionVisible`,
// `grantsAdministration`, the section href tables) so visibility and access can
// never drift apart.

/** The four frontend route prefixes, in canonical order. */
const FRONTEND_ROUTE_PREFIXES: ReadonlyArray<readonly [string, FrontendKey]> = [
    ['/desk', 'desk'],
    ['/ops', 'ops'],
    ['/comply', 'comply'],
    ['/view', 'view'],
];

/**
 * Resolve the frontend a pathname belongs to from its leading segment, or
 * `null` for a non-frontend path (`/`, `/login`, `/not-authorised`). Matches on
 * a segment boundary so `/viewer` never resolves to the `view` shell.
 */
export function frontendForPathname(pathname: string): FrontendKey | null {
    for (const [prefix, key] of FRONTEND_ROUTE_PREFIXES) {
        if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return key;
    }
    return null;
}

/**
 * True when `pathname` is at, or nested under, `href` — matched on a segment
 * boundary so `/desk/suitability/anything` matches `/desk/suitability` but
 * `/desk/suitability-extra` does not.
 */
function pathnameUnderHref(pathname: string, href: string): boolean {
    return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * The most specific route (canonical `href` or any `alias`) a section owns for
 * this pathname, or `null` if the section does not own it. Returns the matched
 * route string so callers can pick the longest match across sections.
 */
function sectionRouteMatch(section: SidebarSection, pathname: string): string | null {
    const routes = [section.href, ...(section.aliases ?? [])];
    let best: string | null = null;
    for (const route of routes) {
        if (!pathnameUnderHref(pathname, route)) continue;
        if (!best || route.length > best.length) best = route;
    }
    return best;
}

/**
 * The sidebar section a pathname falls under, by LONGEST-prefix match within the
 * frontend's section table (canonical href OR alias) — or `null` for a frontend
 * route that no section owns (the shell index `/desk`, or a detail route like
 * `/desk/fact-find/:id` whose section href is not itself a sidebar row).
 */
export function sectionForPathname(frontend: FrontendKey, pathname: string): SidebarSection | null {
    let best: SidebarSection | null = null;
    let bestLen = -1;
    for (const section of sectionsForFrontend(frontend)) {
        const match = sectionRouteMatch(section, pathname);
        if (match === null) continue;
        if (match.length > bestLen) {
            best = section;
            bestLen = match.length;
        }
    }
    return best;
}

/**
 * Whether the ACTIVE persona (with its granted tokens) may render the page at
 * `pathname`. The frontend guard mirror of the backend 403 — derived from the
 * same policy as the sidebar:
 *
 *   1. Non-frontend paths (`/`, `/login`, `/not-authorised`) are always allowed
 *      — they are not behind a frontend shell.
 *   2. The active persona must drive the pathname's frontend
 *      (`personaToFrontend`) — a desk-only user opening `/ops/*` is barred
 *      (cross-frontend).
 *   3. A route on `IM_ASSISTANT_BARRED_DESK_ROUTES` is barred for an
 *      `im-assistant`, EVEN when its section is allowed. The list is empty
 *      today, and the mechanism stays for a later ruling.
 *   4. If a sidebar section owns the pathname, that section must be VISIBLE to
 *      the persona (`visibleSectionsFor`): an `im-assistant` is barred from
 *      `/desk/vulnerability` and the client-model tiers (and their sub-paths),
 *      and a non-admin is barred from `/view/administration` (and its
 *      sub-paths).
 *   5. A frontend route no section owns (shell index, a detail route whose
 *      parent section is authorised) is allowed once the frontend matches.
 */
export function isRouteAuthorised(
    pathname: string,
    activePersona: Persona,
    grantedTokens: readonly string[],
): boolean {
    const requiredFrontend = frontendForPathname(pathname);
    if (requiredFrontend === null) return true;

    if (personaToFrontend(activePersona) !== requiredFrontend) return false;

    if (isDeskRouteBarred(activePersona, pathname)) return false;

    const section = sectionForPathname(requiredFrontend, pathname);
    if (section === null) return true;

    return visibleSectionsFor(activePersona, grantedTokens).some((s) => s.key === section.key);
}

/**
 * Whether a user with these granted `personas` can open `pathname` at all.
 *
 * A link must show only when this is true, because a route that the user
 * cannot open ends on the 403 page. A non-frontend path is always open. For a
 * frontend path, the user must hold the frontend, and the persona that runs
 * the frontend must pass `isRouteAuthorised`. The query string and the
 * fragment do not count.
 */
export function canOpenRoute(pathname: string, personas: readonly string[]): boolean {
    const path = pathname.split(/[?#]/)[0] ?? pathname;
    const frontend = frontendForPathname(path);
    if (frontend === null) return true;
    if (!grantedFrontends(personas).includes(frontend)) return false;
    return isRouteAuthorised(path, intendedPersonaForFrontend(frontend, personas), personas);
}
